import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useTheme, adminTheme } from '@/context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Briefcase,
  Users,
  MessageSquare,
  FolderKanban,
  FileText,
  TrendingUp,
  Clock,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function Dashboard() {
  const { isDark } = useTheme();
  const [stats, setStats] = useState(null);
  const [recentApplications, setRecentApplications] = useState([]);
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, appsRes, enqRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/applications`),
        axios.get(`${API}/enquiries`)
      ]);
      setStats(statsRes.data);
      setRecentApplications(appsRes.data.slice(0, 5));
      setRecentEnquiries(enqRes.data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    {
      title: 'Total Jobs',
      value: stats.total_jobs,
      subtitle: `${stats.active_jobs} active`,
      icon: Briefcase,
      gradient: 'gradient-blue',
      trend: '+12%',
      trendUp: true,
      link: '/admin/applied-jobs'
    },
    {
      title: 'Applications',
      value: stats.total_applications,
      subtitle: `${stats.pending_applications} pending`,
      icon: Users,
      gradient: 'gradient-green',
      trend: '+8%',
      trendUp: true,
      link: '/admin/applied-jobs'
    },
    {
      title: 'Enquiries',
      value: stats.total_enquiries,
      subtitle: `${stats.new_enquiries} new`,
      icon: MessageSquare,
      gradient: 'gradient-purple',
      trend: '+5%',
      trendUp: true,
      link: '/admin/enquiries'
    },
    {
      title: 'Projects',
      value: stats.total_projects,
      subtitle: `${stats.active_projects} in progress`,
      icon: FolderKanban,
      gradient: 'gradient-orange',
      trend: '-2%',
      trendUp: false,
      link: '/admin/projects'
    },
  ] : [];

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    reviewed: 'bg-blue-100 text-blue-700',
    shortlisted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    hired: 'bg-emerald-100 text-emerald-700',
    new: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    resolved: 'bg-green-100 text-green-700'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Page Header */}
      <div>
        <h1 className={`text-2xl font-bold ${adminTheme.textPrimary(isDark)}`}>Dashboard</h1>
        <p className={`mt-1 ${adminTheme.textSecondary(isDark)}`}>Welcome back! Here's what's happening with your business.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link key={index} to={stat.link}>
              <Card className={`cursor-pointer transition-all duration-300 border ${adminTheme.cardBg(isDark)} ${adminTheme.cardHover(isDark)}`} data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm font-medium ${adminTheme.textSecondary(isDark)}`}>{stat.title}</p>
                      <p className={`text-3xl font-bold mt-2 ${adminTheme.textPrimary(isDark)}`}>{stat.value}</p>
                      <p className={`text-sm mt-1 ${adminTheme.textMuted(isDark)}`}>{stat.subtitle}</p>
                    </div>
                    <div className={cn('p-3 rounded-xl', stat.gradient)}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className={`flex items-center mt-4 pt-4 border-t ${adminTheme.border(isDark)}`}>
                    <span className={cn(
                      'flex items-center text-sm font-medium',
                      stat.trendUp ? 'text-green-500' : 'text-red-500'
                    )}>
                      {stat.trendUp ? (
                        <ArrowUpRight className="w-4 h-4 mr-1" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 mr-1" />
                      )}
                      {stat.trend}
                    </span>
                    <span className={`text-sm ml-2 ${adminTheme.textMuted(isDark)}`}>vs last month</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Applications */}
        <div className="lg:col-span-2">
          <Card className={`border ${adminTheme.cardBg(isDark)}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className={`text-lg font-semibold ${adminTheme.textPrimary(isDark)}`}>Recent Applications</CardTitle>
              <Link to="/admin/applied-jobs" className="text-sm text-blue-500 hover:text-blue-400 font-medium">
                View all
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentApplications.map((app) => (
                  <div
                    key={app.id}
                    className={`flex items-center justify-between p-4 rounded-lg transition-colors ${adminTheme.accentBg(isDark)} ${adminTheme.cardHover(isDark)}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium">
                        {app.applicant_name.charAt(0)}
                      </div>
                      <div>
                        <p className={`font-medium ${adminTheme.textPrimary(isDark)}`}>{app.applicant_name}</p>
                        <p className={`text-sm ${adminTheme.textSecondary(isDark)}`}>{app.job_title}</p>
                      </div>
                    </div>
                    <Badge className={cn('capitalize', statusColors[app.status])}>
                      {app.status.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
                {recentApplications.length === 0 && (
                  <p className={`text-center py-8 ${adminTheme.textMuted(isDark)}`}>No recent applications</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Enquiries */}
        <div>
          <Card className={`border ${adminTheme.cardBg(isDark)}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className={`text-lg font-semibold ${adminTheme.textPrimary(isDark)}`}>New Enquiries</CardTitle>
              <Link to="/admin/enquiries" className="text-sm text-blue-500 hover:text-blue-400 font-medium">
                View all
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentEnquiries.map((enq) => (
                  <div key={enq.id} className={`p-4 rounded-lg ${adminTheme.accentBg(isDark)}`}>
                    <div className="flex items-start justify-between mb-2">
                      <p className={`font-medium truncate pr-2 ${adminTheme.textPrimary(isDark)}`}>{enq.subject}</p>
                      <Badge className={cn('capitalize text-xs', statusColors[enq.status])}>
                        {enq.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className={`text-sm truncate ${adminTheme.textSecondary(isDark)}`}>{enq.name}</p>
                    <div className={`flex items-center text-xs mt-2 ${adminTheme.textMuted(isDark)}`}>
                      <Clock className="w-3 h-3 mr-1" />
                      {new Date(enq.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
                {recentEnquiries.length === 0 && (
                  <p className={`text-center py-8 ${adminTheme.textMuted(isDark)}`}>No enquiries yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link to="/admin/blogs">
          <Card className="border-0 shadow-md cursor-pointer hover:shadow-lg transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Blog Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl gradient-pink">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats?.total_blogs || 0}</p>
                    <p className="text-sm text-slate-500">Total Posts</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-900">{stats?.published_blogs || 0}</p>
                  <p className="text-sm text-slate-500">Published</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl gradient-cyan">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">92%</p>
                  <p className="text-sm text-slate-500">Success Rate</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-600">+15%</p>
                <p className="text-sm text-slate-500">This Month</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
